import { useCallback, useEffect, useState } from 'react';

const KEY = 'localflower.closedInquiries';

const read = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
};

const write = next => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  }
};

/**
 * React hook for managing the per-device list of conversations the current
 * user has marked as resolved/closed. Storage is local to the browser, so a
 * user closing a chat on phone won't affect what they see on desktop. We can
 * promote this to user.publicData or Firestore later if cross-device sync is
 * needed.
 */
export const useClosedInquiries = () => {
  const [closed, setClosed] = useState(() => read());

  useEffect(() => {
    const handler = () => setClosed(read());
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handler);
      }
    };
  }, []);

  const close = useCallback(txId => {
    if (!txId) return;
    const next = { ...read(), [txId]: new Date().toISOString() };
    write(next);
    setClosed(next);
  }, []);

  const reopen = useCallback(txId => {
    if (!txId) return;
    const next = read();
    delete next[txId];
    write(next);
    setClosed(next);
  }, []);

  const isClosed = useCallback(txId => !!closed[txId], [closed]);

  return { closed, close, reopen, isClosed };
};
